import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Alert rules as data (gitaumoses4/chukua#94; docs/technical-design.md → Alert rules as data). A rule is a type, thresholds for that
 * type, a scope, a severity, who it tells and a cooldown. Scope is the whole company, or a list of truck classes or units (a truck and its trailer, never one without the other);
 * route and zone scope wait for routes (#45) and a link between a zone and the vendor's geofence id (docs/modules/alerts.md).
 */

export const ALERT_RULE_TYPES = [
  'tracker_silent',
  'geofence',
  'speeding',
  'stopped',
  'off_route',
  'unsafe_stop',
  'speeding_sustained',
  'idling',
  'fuel_drop',
  'tracker_tamper',
  'milestone_missing',
  'milestone_far',
] as const;
export type AlertRuleType = (typeof ALERT_RULE_TYPES)[number];

export const ALERT_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type AlertSeverity = (typeof ALERT_SEVERITIES)[number];

/**
 * `company` is every truck; the others name which: truck class codes, or units. A unit is a truck and the trailer it pulls, named by the
 * truck's id (docs/modules/fleet.md): the office never picks one without the other, and a trailer has no tracker of its own.
 */
export const ALERT_SCOPE_KINDS = ['company', 'truck_class', 'unit'] as const;
export type AlertScopeKind = (typeof ALERT_SCOPE_KINDS)[number];

export const GEOFENCE_DIRECTIONS = ['enter', 'exit'] as const;

const MAX_SILENT_MINUTES = 7 * 24 * 60;
const MAX_SPEED_LIMIT_KPH = 300;
const MAX_FUEL_DROP_LITRES = 2000;
const MAX_FUEL_DROP_WINDOW_MINUTES = 24 * 60;
const MAX_COOLDOWN_SECONDS = 30 * 24 * 60 * 60;
const MAX_SCOPE_VALUES = 100;

export const trackerSilentThresholdsSchema = z.object({ silentMinutes: z.number().int().min(1).max(MAX_SILENT_MINUTES) });
/** `zoneKey` is the vendor's own geofence id, as `GEOFENCE_CROSSED` carries it. */
export const geofenceThresholdsSchema = z.object({ zoneKey: z.string().trim().min(1).max(200), direction: z.enum(GEOFENCE_DIRECTIONS) });
export const speedingThresholdsSchema = z.object({ limitKph: z.number().gt(0).max(MAX_SPEED_LIMIT_KPH) });

/** A tank falling by at least `dropLitres` within `withinMinutes`, with no refill recorded near it (#98). */
export const fuelDropThresholdsSchema = z.object({
  dropLitres: z.number().gt(0).max(MAX_FUEL_DROP_LITRES),
  withinMinutes: z.number().int().min(1).max(MAX_FUEL_DROP_WINDOW_MINUTES),
});
/**
 * Three causes, each switched on or off (#98): the device says external power was cut, the tracker has been silent for
 * `silentWithIgnitionOnMinutes` while its last known ignition was on (null = off), or a phone on the truck's trip is still reporting
 * while the tracker is silent. At least one must be on, or the rule could never fire.
 */
export const trackerTamperThresholdsSchema = z
  .object({
    onPowerDisconnect: z.boolean(),
    silentWithIgnitionOnMinutes: z.number().int().min(1).max(MAX_SILENT_MINUTES).nullable(),
    phoneMovingWhileSilent: z.boolean(),
  })
  .refine((t) => t.onPowerDisconnect || t.silentWithIgnitionOnMinutes !== null || t.phoneMovingWhileSilent, {
    message: 'Switch on at least one cause',
    path: ['onPowerDisconnect'],
  });

/**
 * The movement rules (#96) judge a truck on a trip underway from its stored fixes. A day is the longest window any of them reads, which
 * bounds how many fixes one sweep loads per truck.
 */
export const MAX_MOVEMENT_MINUTES = 24 * 60;
export const MAX_CORRIDOR_METRES = 50_000;
const movementMinutes = z.number().int().min(1).max(MAX_MOVEMENT_MINUTES);

export const stoppedThresholdsSchema = z.object({ stoppedMinutes: movementMinutes });
export const idlingThresholdsSchema = z.object({ idleMinutes: movementMinutes });
export const offRouteThresholdsSchema = z.object({ toleranceMeters: z.number().int().min(10).max(MAX_CORRIDOR_METRES), minutes: movementMinutes });
export const speedingSustainedThresholdsSchema = z.object({ limitKph: z.number().gt(0).max(MAX_SPEED_LIMIT_KPH), minutes: movementMinutes });
/** `overnightMinutes` is how much of a stop must fall in the local night to count; left out, only a stop in an unsafe area raises. */
export const unsafeStopThresholdsSchema = z.object({ stoppedMinutes: movementMinutes, overnightMinutes: movementMinutes.optional() });

/**
 * Contradictions between GPS and a driver's taps (#92). `milestone_missing`: the truck was seen arriving at or leaving a stop and no tap
 * or office entry followed within `windowMinutes`. `milestone_far`: a tap whose stop is farther than `distanceMetres` from every
 * tracker fix within `aroundMinutes` of the tap. `nudgeDriver` also pushes the trip's driver a generic prompt.
 */
export const milestoneMissingThresholdsSchema = z.object({ windowMinutes: movementMinutes, nudgeDriver: z.boolean() });
export const milestoneFarThresholdsSchema = z.object({
  distanceMetres: z.number().int().min(50).max(MAX_CORRIDOR_METRES),
  aroundMinutes: z.number().int().min(1).max(60),
  nudgeDriver: z.boolean(),
});

export type TrackerSilentThresholds = z.output<typeof trackerSilentThresholdsSchema>;
export type GeofenceThresholds = z.output<typeof geofenceThresholdsSchema>;
export type SpeedingThresholds = z.output<typeof speedingThresholdsSchema>;
export type FuelDropThresholds = z.output<typeof fuelDropThresholdsSchema>;
export type TrackerTamperThresholds = z.output<typeof trackerTamperThresholdsSchema>;
export type StoppedThresholds = z.output<typeof stoppedThresholdsSchema>;
export type IdlingThresholds = z.output<typeof idlingThresholdsSchema>;
export type OffRouteThresholds = z.output<typeof offRouteThresholdsSchema>;
export type SpeedingSustainedThresholds = z.output<typeof speedingSustainedThresholdsSchema>;
export type UnsafeStopThresholds = z.output<typeof unsafeStopThresholdsSchema>;
export type MilestoneMissingThresholds = z.output<typeof milestoneMissingThresholdsSchema>;
export type MilestoneFarThresholds = z.output<typeof milestoneFarThresholdsSchema>;

const ruleBase = {
  name: z.string().trim().min(1).max(120),
  scopeKind: z.enum(ALERT_SCOPE_KINDS),
  /** Truck class codes or unit (truck) ids by `scopeKind`; empty for the company. */
  scopeValues: z.array(z.string().trim().min(1).max(60)).max(MAX_SCOPE_VALUES).default([]),
  severity: z.enum(ALERT_SEVERITIES),
  notifyRoleKeys: z.array(z.string().trim().min(1)).max(20).default([]),
  notifyUserIds: z.array(z.uuid()).max(50).default([]),
  /** After an alert is resolved, the same rule stays quiet on that truck for this long. */
  cooldownSeconds: z.number().int().min(1).max(MAX_COOLDOWN_SECONDS),
};

const withThresholds = <T extends AlertRuleType, S extends z.ZodType>(type: T, thresholds: S) => z.object({ ...ruleBase, type: z.literal(type), thresholds });

export const alertRuleInputSchema = z
  .discriminatedUnion('type', [
    withThresholds('tracker_silent', trackerSilentThresholdsSchema),
    withThresholds('geofence', geofenceThresholdsSchema),
    withThresholds('speeding', speedingThresholdsSchema),
    withThresholds('fuel_drop', fuelDropThresholdsSchema),
    withThresholds('tracker_tamper', trackerTamperThresholdsSchema),
    withThresholds('stopped', stoppedThresholdsSchema),
    withThresholds('off_route', offRouteThresholdsSchema),
    withThresholds('unsafe_stop', unsafeStopThresholdsSchema),
    withThresholds('speeding_sustained', speedingSustainedThresholdsSchema),
    withThresholds('idling', idlingThresholdsSchema),
    withThresholds('milestone_missing', milestoneMissingThresholdsSchema),
    withThresholds('milestone_far', milestoneFarThresholdsSchema),
  ])
  .refine((rule) => (rule.scopeKind === 'company') === (rule.scopeValues.length === 0), {
    message: 'Pick at least one truck class or unit, and none for the whole company',
    path: ['scopeValues'],
  })
  .refine((rule) => rule.scopeKind !== 'unit' || rule.scopeValues.every((value) => z.uuid().safeParse(value).success), {
    message: 'A unit is named by its truck id',
    path: ['scopeValues'],
  });
export type AlertRuleInput = z.output<typeof alertRuleInputSchema>;

const enabledFilter = z
  .enum(['true', 'false'])
  .transform((value) => value === 'true')
  .optional();

/** `GET /alert-rules` — search matches the name. `enabled` is `true`/`false` text, never coerced (CLAUDE.md → Lists and tables). */
export const alertRuleListQuerySchema = defineListQuery({
  sortFields: ['name', 'type', 'severity', 'createdAt'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: {
    type: listFilter(z.enum(ALERT_RULE_TYPES)),
    severity: listFilter(z.enum(ALERT_SEVERITIES)),
    scopeKind: listFilter(z.enum(ALERT_SCOPE_KINDS)),
    enabled: enabledFilter,
  },
});
export type AlertRuleListQuery = z.infer<typeof alertRuleListQuerySchema>;

export interface AlertRuleView {
  id: string;
  name: string;
  type: AlertRuleType;
  thresholds:
    | TrackerSilentThresholds
    | GeofenceThresholds
    | SpeedingThresholds
    | FuelDropThresholds
    | TrackerTamperThresholds
    | StoppedThresholds
    | IdlingThresholds
    | OffRouteThresholds
    | SpeedingSustainedThresholds
    | UnsafeStopThresholds
    | MilestoneMissingThresholds
    | MilestoneFarThresholds;
  scopeKind: AlertScopeKind;
  scopeValues: string[];
  severity: AlertSeverity;
  notifyRoleKeys: string[];
  notifyUserIds: string[];
  cooldownSeconds: number;
  isEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}
